import React from 'react';
import { FaRocket, FaLightbulb, FaBolt, FaBrain, FaLinkedin } from 'react-icons/fa';


const About = () => {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-[#0a1220] transition-colors duration-300">

      {/* Hero Section - Mission */}
      <div className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-4xl mx-auto text-center relative z-10">
          <span className="inline-block py-1 px-3 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-sm font-semibold mb-6 tracking-wide">
            OUR MISSION
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6 leading-tight">
            Bridging the gap between <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#f97316] to-[#fb923c]">theory and real-world practice</span>.
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-2xl mx-auto">
            DataHelix exists to empower aspiring data professionals with the practical tools, interactive exercises, and deep insights needed to master data analytics - not just memorize syntax.
          </p>
        </div>
      </div>

      {/* The Story / Why Section */}
      <div className="py-16 px-6 bg-white dark:bg-[#0f172a] border-y border-gray-200 dark:border-gray-800 transition-colors duration-300">
        <div className="max-w-[1000px] mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
              Why we built this
            </h2>
            <div className="w-20 h-1.5 bg-[#f97316] rounded-full mb-6"></div>
            <div className="space-y-4 text-gray-600 dark:text-gray-300 text-lg leading-relaxed">
              <p>
                Learning SQL and Data Science often feels like staring at a static textbook. You learn the commands, but you don't learn how to <em>think</em> in data.
              </p>
              <p>
                We wanted to create a platform that feels alive. Where you don't just write queries, but solve actual business problems. Where you can experiment, fail, fix, and truly understand the "why" behind every line of code.
              </p>
            </div>


          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-6 bg-gray-50 dark:bg-[#1e293b] rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
              <div className="text-3xl text-[#f97316] mb-3"><FaRocket /></div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-1">Interactive</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Run code directly in your browser.</p>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-[#1e293b] rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all mt-8">
              <div className="text-3xl text-[#f97316] mb-3"><FaLightbulb /></div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-1">Practical</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Real-world business use cases.</p>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-[#1e293b] rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all">
              <div className="text-3xl text-[#f97316] mb-3"><FaBolt /></div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-1">Fast</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Instant feedback, no setup required.</p>
            </div>
            <div className="p-6 bg-gray-50 dark:bg-[#1e293b] rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all mt-8">
              <div className="text-3xl text-[#f97316] mb-3"><FaBrain /></div>
              <h3 className="font-bold text-gray-900 dark:text-white mb-1">Deep</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Goes beyond the basics.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Connect / Team Section */}
      <div className="py-12 px-6 text-center">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8">
          Ready to dive in?
        </h2>

        <div className="flex justify-center mb-10">
          <a href="https://www.linkedin.com/company/datahelix-io/" target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#0077b5] hover:bg-[#006396] text-white rounded-xl transition-all shadow-lg hover:shadow-xl text-lg font-bold transform hover:-translate-y-1">
            <FaLinkedin size={24} />
            Follow DataHelix on LinkedIn
          </a>
        </div>

        <div className="max-w-2xl mx-auto pt-8 border-t border-gray-200 dark:border-gray-800">
          <p className="text-gray-500 dark:text-gray-400 mb-6 text-sm uppercase tracking-wider font-semibold">Contact Us</p>
          <div className="flex flex-col md:flex-row justify-center items-center gap-8 md:gap-12">
            <div className="flex flex-col text-left">
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-900 dark:text-white text-lg">Vikrant Kumar</span>
                <a href="https://www.linkedin.com/in/vikrantkumar95/" target="_blank" rel="noopener noreferrer"
                  className="p-1.5 rounded-full text-gray-400 hover:text-[#0077b5] hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-all">
                  <FaLinkedin size={20} />
                </a>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Analytics @ Teya, London</span>
            </div>

            <div className="hidden md:block w-px h-12 bg-gray-200 dark:bg-gray-800"></div>

            <div className="flex flex-col text-left">
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-900 dark:text-white text-lg">Parth Mittal</span>
                <a href="https://www.linkedin.com/in/parth-mittal1/" target="_blank" rel="noopener noreferrer"
                  className="p-1.5 rounded-full text-gray-400 hover:text-[#0077b5] hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-all">
                  <FaLinkedin size={20} />
                </a>
              </div>
              <span className="text-sm text-gray-500 dark:text-gray-400">Analytics @ Sanofi, Paris</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
